// Persistent HUD inside the Beginner Mode shell. Now shows:
//   • avatar (signed-in users) / generic guest pip
//   • streak flame (signed-in users only)
//   • progress bar + skills counter
//   • sound mute toggle
//   • live hackathon pot
//   • daily bonus claim button (one-tap +25 XP / day)
import { useEffect, useRef, useState } from "react";
import {
  Trophy,
  Target,
  Sparkles,
  Flame,
  Volume2,
  VolumeX,
  Gift,
  UserCircle2,
} from "lucide-react";
import { AI_MASTERY_PATH } from "@/data/aiMasteryPath";
import { useHackathon, formatUsd } from "@/hooks/useHackathon";
import { useStreak } from "@/hooks/useStreak";
import { useMyProfile } from "@/hooks/useMyProfile";
import { useDailyBonus } from "@/hooks/useDailyBonus";
import {
  isSkillSoundsMuted,
  setSkillSoundsMuted,
  playCoin,
} from "@/lib/skillSounds";

interface Props {
  currentNum: number;
  completedCount: number;
  xpBumpKey?: number;
  xpAmount?: number;
  onBonusClaimed?: (amount: number) => void;
}

const BeginnerProgressHud = ({
  currentNum,
  completedCount,
  xpBumpKey = 0,
  xpAmount = 50,
  onBonusClaimed,
}: Props) => {
  const { pot } = useHackathon({ scoreboardLimit: 1 });
  const { streak } = useStreak();
  const { profile } = useMyProfile();
  const { available: bonusAvailable, amount: bonusAmount, claim } = useDailyBonus();

  const potLabel = pot ? formatUsd(pot.pot_cents) : "$10.4K";
  const total = AI_MASTERY_PATH.length;
  const pct = Math.round((completedCount / total) * 100);

  const [bumpVisible, setBumpVisible] = useState(false);
  const [bumpAmount, setBumpAmount] = useState(xpAmount);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setBumpAmount(xpAmount);
    setBumpVisible(true);
    const t = window.setTimeout(() => setBumpVisible(false), 1600);
    return () => window.clearTimeout(t);
  }, [xpBumpKey, xpAmount]);

  // Sound toggle — local state mirrors the persisted flag so the icon
  // updates immediately on click without waiting for the storage event.
  const [muted, setMuted] = useState(() => isSkillSoundsMuted());
  useEffect(() => {
    const sync = () => setMuted(isSkillSoundsMuted());
    window.addEventListener("hl:skillSounds:change", sync);
    return () => window.removeEventListener("hl:skillSounds:change", sync);
  }, []);
  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setSkillSoundsMuted(next);
  };

  const handleClaimBonus = () => {
    if (!claim()) return;
    playCoin();
    onBonusClaimed?.(bonusAmount);
    // Reuse the floater animation to surface "+25 XP".
    setBumpAmount(bonusAmount);
    setBumpVisible(true);
    window.setTimeout(() => setBumpVisible(false), 1600);
  };

  const initial = (profile?.displayName || profile?.username || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <div className="container mx-auto px-4 pt-3">
      <div className="relative rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-3 py-2 flex items-center gap-3">
        {/* Avatar / guest pip */}
        <div className="shrink-0">
          {profile?.avatarUrl ? (
            <img
              src={profile.avatarUrl}
              alt=""
              className="w-7 h-7 rounded-full border border-primary/40 object-cover"
            />
          ) : profile ? (
            <div className="w-7 h-7 rounded-full border border-primary/40 bg-primary/20 grid place-items-center text-[11px] font-mono font-bold text-primary">
              {initial}
            </div>
          ) : (
            <div
              className="w-7 h-7 rounded-full border border-border bg-card grid place-items-center text-muted-foreground"
              title="Sign in to save your streak across devices"
            >
              <UserCircle2 className="w-4 h-4" />
            </div>
          )}
        </div>

        {/* Streak — only meaningful for signed-in users */}
        {profile && streak > 0 && (
          <div
            className="hidden sm:inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5"
            title={`${streak}-day streak`}
          >
            <Flame className="w-3 h-3 text-primary" />
            <span className="text-[11px] font-mono font-bold text-primary tabular-nums">
              {streak}
            </span>
          </div>
        )}

        <div className="flex items-center gap-1.5 shrink-0">
          <Target className="w-3.5 h-3.5 text-primary" />
          <span className="text-[11px] font-mono font-bold text-foreground tabular-nums">
            {Math.max(completedCount, 0)}/{total}
          </span>
          <span className="hidden md:inline text-[10px] font-mono text-muted-foreground uppercase tracking-wider">
            skills
          </span>
        </div>

        <div className="flex-1 min-w-0 min-w-[40px]">
          <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-700"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Daily bonus — only when claimable */}
        {bonusAvailable && (
          <button
            type="button"
            onClick={handleClaimBonus}
            className="shrink-0 inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/15 hover:bg-primary/25 px-2 py-0.5 text-[10px] font-mono font-bold text-primary transition-colors"
            title={`Claim daily bonus: +${bonusAmount} XP`}
            aria-label={`Claim daily bonus, plus ${bonusAmount} XP`}
          >
            <Gift className="w-3 h-3" />
            <span className="hidden sm:inline">+{bonusAmount} XP</span>
            <span className="sm:hidden">+{bonusAmount}</span>
          </button>
        )}

        {/* Sound toggle */}
        <button
          type="button"
          onClick={toggleMute}
          className="shrink-0 inline-flex items-center justify-center rounded-full border border-border bg-card/40 hover:bg-card text-muted-foreground hover:text-foreground transition-colors w-6 h-6"
          title={muted ? "Sounds off — click to enable" : "Sounds on — click to mute"}
          aria-label={muted ? "Enable skill completion sounds" : "Mute skill completion sounds"}
          aria-pressed={!muted}
        >
          {muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
        </button>

        {/* Pot */}
        <div className="flex items-center gap-1.5 shrink-0 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5">
          <Trophy className="w-3 h-3 text-primary" />
          <span className="text-[11px] font-mono font-bold text-primary tabular-nums">
            {potLabel}
          </span>
        </div>

        {bumpVisible && (
          <span
            className="pointer-events-none absolute -top-3 right-3 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-mono font-bold text-primary-foreground shadow-lg shadow-primary/40 animate-fade-in"
            aria-live="polite"
          >
            <Sparkles className="w-3 h-3" />
            +{bumpAmount} XP
          </span>
        )}
      </div>
    </div>
  );
};

export default BeginnerProgressHud;
